import dayjs from 'dayjs';
import { capitalize } from 'es-toolkit';

import { DATE_FORMATS } from '@/shared/utils/date/date-formats';
import { PostFilter } from '../../../../entities/post/models';
import type { BreadcrumbSegment } from '../ui/filter-breadcrumb';
import { FILTER_TYPE_LABELS } from './filter-mappings';

/**
 * 브레드크럼 세그먼트 생성
 * @param queryName 쿼리 이름
 * @param searchParams 쿼리 파라미터
 * @returns 브레드크럼 세그먼트
 * @description
 *  1. 기본적으로 FILTER_TYPE_LABELS에 정의된 필드에 대해서만 breadcrumb에 추가
 *  2. Date, Sentiment/Theme에 대해서는 별도 처리
 *   - 두개 이상의 필드를 조합하여 표시하는 경우
 */

export const buildBreadcrumbs = (
  queryName: string,
  searchParams: URLSearchParams,
): BreadcrumbSegment[] => {
  const segments: BreadcrumbSegment[] = [];

  if (queryName) {
    segments.push({ label: queryName });
  }

  // Add date range first if present
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  if (startDate && endDate) {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const dateLabel = start.isSame(end, 'day')
      ? start.format(DATE_FORMATS.base)
      : `${start.format(DATE_FORMATS.base)} - ${end.format(DATE_FORMATS.base)}`;
    segments.push({ label: dateLabel });
  }

  // Get dimension to determine breadcrumb order
  const dimension = searchParams.get('dimension');

  // Build segments based on dimension (drill down starting point)
  switch (dimension) {
    case PostFilter.Theme: {
      // Theme-based drill down: ${sentiment} Theme > ${theme} > Brand > ${brand} > Product > ${product}
      const sentiment = searchParams.get(PostFilter.Sentiment);
      const theme = searchParams.get(PostFilter.Theme);
      const brand = searchParams.get(PostFilter.Brand);
      const product = searchParams.get(PostFilter.Product);

      if (sentiment && theme) {
        segments.push({ label: `${capitalize(sentiment)} Theme` });
        segments.push({ label: theme });
      }
      if (brand) {
        segments.push({ label: 'Brand' });
        segments.push({ label: brand });
      }
      if (product) {
        segments.push({ label: 'Product' });
        segments.push({ label: product });
      }
      break;
    }

    case PostFilter.Brand: {
      // Brand-based drill down: Brand > ${brand} > ${sentiment} Theme > ${theme}
      const brand = searchParams.get(PostFilter.Brand);
      const sentiment = searchParams.get(PostFilter.Sentiment);
      const theme = searchParams.get(PostFilter.Theme);

      if (brand) {
        segments.push({ label: 'Brand' });
        segments.push({ label: brand });
      }
      if (sentiment && theme) {
        segments.push({ label: `${capitalize(sentiment)} Theme` });
        segments.push({ label: theme });
      }
      break;
    }

    case PostFilter.Product: {
      // Product-based drill down: Product > ${product} > ${sentiment} Theme > ${theme}
      const product = searchParams.get(PostFilter.Product);
      const sentiment = searchParams.get(PostFilter.Sentiment);
      const theme = searchParams.get(PostFilter.Theme);

      if (product) {
        segments.push({ label: 'Product' });
        segments.push({ label: product });
      }
      if (sentiment && theme) {
        segments.push({ label: `${capitalize(sentiment)} Theme` });
        segments.push({ label: theme });
      }
      break;
    }

    default: {
      // Fallback to original logic for other dimensions
      const entries = searchParams.entries();
      entries.forEach(([key, value]) => {
        switch (key) {
          case 'endDate':
          case 'startDate':
          case 'dimension':
            break;
          case PostFilter.Sentiment: {
            const sentiment = value;
            const theme = searchParams.get(PostFilter.Theme);
            if (theme) {
              segments.push({ label: `${capitalize(sentiment)} Theme` });
              segments.push({ label: theme });
            }
            break;
          }
          default: {
            const keyName = FILTER_TYPE_LABELS[key];
            if (keyName) {
              segments.push({ label: FILTER_TYPE_LABELS[key] });
              segments.push({ label: value });
            }
            break;
          }
        }
      });
      break;
    }
  }

  return segments;
};
